import { z } from 'zod'

import { TaskType } from '../ag-concepts'
import { ReportType } from './seasonal-task-report'

export enum InputReportToggle {
  IncludeClientData = 'includeClients',
  IncludeFarmData = 'includeFarms',
  IncludePassData = 'includePasses'
}

export enum CondensedReportToggle {
  Seeding = 'seeding',
  Granular = 'granular'
}

export enum AgronomicReportToggle {
  Granular = 'granular',
  Force = 'force',
  Global = 'global',
  GranularProducts = 'granularProducts',
  Harvest = 'harvest',
  HarvestLoadList = 'harvestLoadList',
  Hybrids = 'hybrids',
  Implement = 'implement',
  LinearPosition = 'linearPosition',
  Liquid = 'liquid',
  LiquidProducts = 'liquidProducts',
  Pressure = 'pressure',
  Seeding = 'seeding',
  Thumbnails = 'thumbnail',
  Weather = 'weather'
}

export type IncludedDataToggle = CondensedReportToggle | InputReportToggle | AgronomicReportToggle

/**
 * Every toggle any report type accepts, for parsing a request whose toggles were not chosen from one
 * report's own set. Each type honors a subset ({@link getTogglesForReportType}), so parsing against
 * this does not establish that a toggle applies to the report being built.
 */
export const includedDataToggleSchema = z.enum({
  ...InputReportToggle,
  ...CondensedReportToggle,
  ...AgronomicReportToggle
})

export type PassDataFilters = Record<TaskType, boolean>

export const toggleTranslationKeys: Record<IncludedDataToggle, string> = {
  [InputReportToggle.IncludeClientData]: 'reporting.tasks.showClients',
  [InputReportToggle.IncludeFarmData]: 'reporting.tasks.showFarms',
  [InputReportToggle.IncludePassData]: 'reporting.tasks.showPasses',
  [CondensedReportToggle.Seeding]: 'agConcepts.applicationTypes.seeding',
  [CondensedReportToggle.Granular]: 'agConcepts.applicationTypes.granular',
  [AgronomicReportToggle.Force]: 'agConcepts.applicationTypes.force',
  [AgronomicReportToggle.Global]: 'agConcepts.applicationTypes.global',
  [AgronomicReportToggle.GranularProducts]: 'reporting.tasks.granularProducts',
  [AgronomicReportToggle.Harvest]: 'agConcepts.applicationTypes.harvest',
  [AgronomicReportToggle.HarvestLoadList]: 'reporting.tasks.harvestLoadList',
  [AgronomicReportToggle.Hybrids]: 'reporting.tasks.hybrids',
  [AgronomicReportToggle.Implement]: 'agConcepts.applicationTypes.implement',
  [AgronomicReportToggle.LinearPosition]: 'agConcepts.applicationTypes.linearposition',
  [AgronomicReportToggle.Liquid]: 'agConcepts.applicationTypes.liquid',
  [AgronomicReportToggle.LiquidProducts]: 'reporting.tasks.liquidProducts',
  [AgronomicReportToggle.Pressure]: 'agConcepts.applicationTypes.pressure',
  [AgronomicReportToggle.Thumbnails]: 'reporting.tasks.thumbnails',
  [AgronomicReportToggle.Weather]: 'weather.weather'
}

export const taskToggleTranslationKeys: Record<TaskType, string> = {
  [TaskType.Plant]: 'labels.tasks.plant',
  [TaskType.Spray]: 'labels.tasks.spray',
  [TaskType.Sidedress]: 'labels.tasks.sidedress',
  [TaskType.Striptill]: 'labels.tasks.striptill',
  [TaskType.Harvest]: 'labels.tasks.harvest'
}

const togglesForReportType = {
  [ReportType.SeasonalInputs]: Object.values(InputReportToggle),
  [ReportType.CondensedSeason]: Object.values(CondensedReportToggle),
  [ReportType.AgronomicActivity]: Object.values(AgronomicReportToggle)
}

export const getTogglesForReportType = (reportType: ReportType): IncludedDataToggle[] => {
  return togglesForReportType[reportType]
}

/**
 * The sections a report carries when nobody has said which they want. A request that names sections
 * is adding to these rather than replacing them, so a caller that names one section does not silently
 * drop the rest.
 */
const defaultTogglesForReportType: Record<ReportType, IncludedDataToggle[]> = {
  [ReportType.SeasonalInputs]: [],
  [ReportType.CondensedSeason]: [CondensedReportToggle.Seeding],
  [ReportType.AgronomicActivity]: [
    CondensedReportToggle.Seeding,
    AgronomicReportToggle.Thumbnails,
    AgronomicReportToggle.Global
  ]
}

export const getDefaultTogglesForReportType = (reportType: ReportType): IncludedDataToggle[] =>
  defaultTogglesForReportType[reportType]
